import { useTranslation } from 'react-i18next';

import { DataPanel, DataPanelAutoWrapInlineItem, DataPanelItem, DataPanelRow, NgButton } from '@/ui';
import type { IncidentAppleEedCapabilities } from '@/signalr';
import type { AppleEedCallInformation, AppleEedCallerInformation } from '@ngsos/portal-core/commands';
import { formatAppleEedHeight, formatAppleEedWeight } from '@ngsos/portal-core/incidents';
import { ClassificationPanelItem, type ClassificationItem } from '../shared/ClassificationPanelItem';
import { AppleEedEmergencyContactPanelItem } from './AppleEedEmergencyContactPanelItem';

export type AppleEedSectionProps = {
  appleEedCallInformation: AppleEedCallInformation | null;
  appleEedMedicalData: AppleEedCallerInformation | null;
  appleEedBatteryLevel: string | null;
  appleEedCallCapabilities: readonly IncidentAppleEedCapabilities[];
  askedForAppleEedMedicalData: boolean;
  appleEedMedicalDataIsLoading: boolean;
  askedForAppleEedMedia: boolean;
  getAppleEedMedicalData: () => void | Promise<void>;
  requestAppleEedMedia: () => void | Promise<void>;
};

export function AppleEedSection({
  appleEedCallInformation,
  appleEedMedicalData,
  appleEedBatteryLevel,
  appleEedCallCapabilities,
  askedForAppleEedMedicalData,
  appleEedMedicalDataIsLoading,
  askedForAppleEedMedia,
  getAppleEedMedicalData,
  requestAppleEedMedia,
}: AppleEedSectionProps) {
  const { t } = useTranslation();

  const hasAdditionalData = appleEedCallCapabilities.some((capability) => capability.hasAdditionalData);
  const hasMediaUpload = appleEedCallCapabilities.some((capability) => capability.hasMediaUpload);

  if (!hasAdditionalData && !hasMediaUpload) {
    return null;
  }

  const mediaRequest = (
    <>
      <NgButton
        text={t('Incident_InfoPanel_AppleEed_RequestMedia')}
        onClick={requestAppleEedMedia}
        icon="fal fa-image"
        enableMultipleClickPrevention
        showProgress
      />
      {askedForAppleEedMedia && <div>{t('Incident_InfoPanel_AppleEed_MediaRequested')}</div>}
    </>
  );

  const classificationItems: ClassificationItem[] = [];
  if (appleEedCallInformation?.trigger_type === 'Fall Detection') {
    classificationItems.push({
      title: t('Incident_InfoPanel_AppleEed_FallDetection'),
      image: 'Images/Incident/AppleEed/fall_detection.svg',
    });
  }
  if (appleEedCallInformation?.trigger_type === 'Crash Detection') {
    classificationItems.push({
      title: t('Incident_InfoPanel_AppleEed_CrashDetection'),
      image: 'Images/Incident/AppleEed/crash_detection.svg',
    });
  }

  return (
    <DataPanel header="Apple EED data">
      {appleEedMedicalDataIsLoading && <AppleEedLoadingPlaceholder />}

      {!appleEedMedicalDataIsLoading &&
        hasAdditionalData &&
        (appleEedMedicalData !== null ? (
          <>
            <ClassificationPanelItem items={classificationItems} />

            {appleEedBatteryLevel != null && (
              <DataPanelAutoWrapInlineItem title={t('Incident_InfoPanel_BatteryLevel')} value={appleEedBatteryLevel} />
            )}

            <DataPanelRow>
              <DataPanelItem
                title={t('Incident_InfoPanel_Age')}
                value={appleEedMedicalData.age?.toString()}
                showEmptyPlaceholder
              />

              <DataPanelItem
                title={t('Incident_InfoPanel_CallerHeight')}
                value={formatAppleEedHeight(appleEedMedicalData.height)}
                contentCssClass="text-center"
                showEmptyPlaceholder
              />

              <DataPanelItem
                title={t('Incident_InfoPanel_CallerWeight')}
                value={formatAppleEedWeight(appleEedMedicalData.weight)}
                contentCssClass="text-end"
                showEmptyPlaceholder
              />
            </DataPanelRow>

            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_Name')}
              value={appleEedMedicalData.name ?? undefined}
              showEmptyPlaceholder
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_GestationalAge')}
              value={appleEedMedicalData.gestational_age ?? undefined}
              showEmptyPlaceholder
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_Allergies')}
              value={appleEedMedicalData.allergies ?? undefined}
              showEmptyPlaceholder
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_BloodType')}
              value={appleEedMedicalData.blood_type ?? undefined}
              showEmptyPlaceholder
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_Medications')}
              value={appleEedMedicalData.medications ?? undefined}
              showEmptyPlaceholder
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_MedicalConditions')}
              value={appleEedMedicalData.medical_conditions ?? undefined}
              showEmptyPlaceholder
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_MedicalNotes')}
              value={appleEedMedicalData.medical_notes ?? undefined}
              showEmptyPlaceholder
            />

            <AppleEedEmergencyContactPanelItem contacts={appleEedMedicalData.emergency_contacts} />
          </>
        ) : askedForAppleEedMedicalData ? (
          <>
            {appleEedBatteryLevel != null && (
              <DataPanelItem title={t('Incident_InfoPanel_BatteryLevel')} value={appleEedBatteryLevel} />
            )}
            <div className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center">
              {t('Incident_InfoPanel_AppleEed_NoAppleEedAdditionalData')}
              {hasMediaUpload && mediaRequest}
            </div>
          </>
        ) : (
          <div className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center">
            {t('Incident_InfoPanel_AppleEed_RetrieveInfo_Line1')}
            <NgButton
              text={t('Incident_InfoPanel_AppleEed_RetrieveInfo')}
              onClick={getAppleEedMedicalData}
              enableMultipleClickPrevention
              showProgress
            />
            {hasMediaUpload && mediaRequest}
          </div>
        ))}

      {!appleEedMedicalDataIsLoading && hasMediaUpload && appleEedMedicalData !== null && (
        <div className="d-flex flex-column align-items-center gap-2 p-2 medium text-center">{mediaRequest}</div>
      )}

      {!appleEedMedicalDataIsLoading && !hasAdditionalData && hasMediaUpload && (
        <div className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center">
          {mediaRequest}
        </div>
      )}
    </DataPanel>
  );
}

function AppleEedLoadingPlaceholder() {
  return (
    <div className="d-flex flex-column gap-1 p-2 placeholder-glow">
      <div className="placeholder col-8"></div>
      <div className="placeholder col-4"></div>
      <div className="placeholder col-7"></div>
      <div className="placeholder col-5"></div>
      <div className="placeholder col-9"></div>
    </div>
  );
}
